import { Link, useSearchParams } from "react-router-dom";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import {
  useSectionCompletionStore,
  sectionKey,
  type SectionKind,
} from "@/stores/sectionCompletionStore";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { StatusIcon } from "@/components/ui/status-icon";
import { LessonStatus } from "@/components/course/tree/types";
import { treeTitleClass } from "@/components/course/tree/treeStyles";
import { buildSectionNavQueryString } from "@/lib/chapterNavigation";
import { cn } from "@/lib/utils";

interface SectionNodeProps {
  /** The module or chapter whose `intro` / `outro` text this node opens. */
  parent: CourseTreeNode;
  kind: SectionKind;
}

const TITLES: Record<SectionKind, string> = {
  intro: "Einführung",
  outro: "Abschluss",
};

/**
 * A standalone "Einführung" (intro) or "Abschluss" (outro) entry. Intros render
 * as the first child of a module/chapter, outros as the last. Opening one
 * navigates to the parent node, which the store renders in the matching view
 * mode (intro is the default; outro is requested via ?view=outro).
 */
const SectionNode = ({ parent, kind }: SectionNodeProps) => {
  const { selectedNodeId, viewMode, focusedNodeId } = useCourseDetailStore();
  const isCompleted = useSectionCompletionStore((s) =>
    s.completed.has(sectionKey(kind, parent.id))
  );
  const basePath = useCourseBasePath();
  const [searchParams] = useSearchParams();

  const title = TITLES[kind];
  const isSelected =
    focusedNodeId == null && selectedNodeId === parent.id && viewMode === kind;
  const to = `${basePath}/${parent.id}${buildSectionNavQueryString(kind, searchParams)}`;

  const statusIcon = (
    <StatusIcon
      status={isCompleted ? LessonStatus.Completed : LessonStatus.NotStarted}
    />
  );

  const titleClasses = cn(
    isSelected ? "font-bold" : "font-semibold",
    treeTitleClass(isSelected)
  );

  // Chapter sections sit inside the subchapter list — mirror SubChapterNode.
  if (parent.type === CourseTreeNodeTypeEnum.Chapter) {
    return (
      <Link
        to={to}
        id={`${kind}-${parent.id}`}
        className={cn(
          "relative block pl-3 pr-4 py-2 transition-colors",
          isSelected && "bg-accent"
        )}
        title={title}
      >
        {isSelected && (
          <span className="absolute left-[-1px] top-1 bottom-1 w-[3px] rounded-r bg-primary" />
        )}
        <div className="flex items-center gap-2">
          {statusIcon}
          <div className="flex-1 min-w-0">
            <div className={cn("text-[14px] leading-snug", titleClasses)}>
              {title}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Module sections sit among the chapters — mirror ChapterNode.
  return (
    <Link
      to={to}
      id={`${kind}-${parent.id}`}
      className={cn(
        "w-full flex items-center gap-2.5 pl-9 pr-5 py-2 text-left transition-colors",
        {
          "bg-accent": isSelected,
          "hover:bg-gray-50": !isSelected,
        }
      )}
      title={title}
    >
      {statusIcon}
      <div className="flex-1 min-w-0">
        <span className={cn("text-[16px] block leading-snug", titleClasses)}>
          {title}
        </span>
      </div>
    </Link>
  );
};

export default SectionNode;
